<template>
    <div ref="wrapperElement" :style="{minHeight: props.height ? `${props.height}px` : undefined}"></div>
</template>

<script setup lang="ts">
    import ApexCharts from 'apexcharts';
	import {ref, watch, onMounted, onBeforeUnmount, nextTick} from "vue";
	import type {Ref} from "vue";
	import {debounce, disableWindowScroll, enableWindowScroll} from "@spoferan/spoferan-ts-core";
	import {useNuxtApp} from "#imports";

	const props = withDefaults(defineProps<{
		// The selected data index of the first series
		hoverIndex?: number,

		type?: string,
		data: ApexAxisChartSeries|ApexNonAxisChartSeries,
		labels?: string[],
		xAnnotations?: XAxisAnnotations[],
		height?: number|string,
		colors?: string[],
		options?: object,
		disableAnimation?: boolean,
		hideLegend?: boolean,
		typeOptions?: object,
		downloadable?: boolean,
	}>(), {
		modelValue: 0,
		type: 'line',
		height: 'auto',
		disableAnimation: false,
		hideLegend: false,
		colors: ['#47a447', '#F5574B', '#1693EB', '#ff8a65', '#ffeb3b', '#ba68c8', '#4dd0e1'],
		options: {},
		xAnnotations: [],
		typeOptions: {},
	});
	const emit = defineEmits(['data-point-click', 'legend-click', 'update:hoverIndex']);

	const {$device} = useNuxtApp();

	const selectedHoverIndex: Ref<number> = ref(props.hoverIndex);
	watch(selectedHoverIndex, (newValue) => {
		emit('update:hoverIndex', newValue);
	});
	watch(() => props.hoverIndex, (newValue) => {
		selectedHoverIndex.value = newValue;
	});

	const wrapperElement = ref(null);
	const chartElement: Ref<ApexCharts|null> = ref(null);

	// Fully updating the data or labels with large datasets will freeze the UI whereby it's faster to destroy and re-render the chart
	watch(() => [props.data, props.labels], () => {
		destroy();
		nextTick(() => {
		    render();
		})
	});

	watch(() => props.colors, (newValue) => {
		chartElement.value?.updateOptions({
			colors: newValue
		});
	});

	watch(() => props.xAnnotations, (newValue) => {
		chartElement.value?.updateOptions({
			annotations: {
				xaxis: newValue
			}
		});
	});

	function destroy() {
		chartElement.value?.destroy();
		chartElement.value = null;
	}

	const scheduleEnableWindowScroll = debounce(function () {
		enableWindowScroll()
	}, 500);

	function render() {
		destroy();

		const options: {[key: string]: any} = {
			chart: {
				type: props.type,
				height: props.height,
				events: {
					dataPointSelection: (event, chartContext, config) => {
						emit('data-point-click', {
							index: config.dataPointIndex,
							label: props.labels && props.labels.length ? props.labels[config.dataPointIndex] : null,
							value: props.data[config.dataPointIndex],
						})
					},
					legendClick: (_, seriesIndex) => {
						emit('legend-click', {
							index: seriesIndex
						})
					},
					mouseMove: (event, chartContext, config) => {
						// Prevent trigger an update when moved out of datapoint to prevent flickering on large datasets, e.g., track charts
						if (config.dataPointIndex > -1) {
							selectedHoverIndex.value = config.dataPointIndex;
						}

						// Disable window scroll when hovering over chart for better mobile experience
						if ($device.isMobile || $device.isTablet) {
							disableWindowScroll();
							scheduleEnableWindowScroll();
						}
					}
				},
				toolbar: {
					tools: {
						download: props.downloadable,
						reset: false,
						zoom: false,
						zoomin: false,
						zoomout: false,
						pan: false
					}
				},
				animations: {enabled: !props.disableAnimation},
				parentHeightOffset: 0,
				...props.typeOptions
			},
			...props.options,
			series: props.data,
			labels: props.labels,
			colors: props.colors,
			annotations: {
				xaxis: props.xAnnotations
			}
		};

		if (props.hideLegend) {
			options.legend = {
				show: false
			};
		}

		chartElement.value = new ApexCharts(wrapperElement.value, options);
		chartElement.value?.render();
	}

	onMounted(() => {
		// We must wait until the wrapper element is fully rendered
		nextTick(() => {
			render();
		});
	})

	onBeforeUnmount(() => {
		destroy()
	})

</script>

<style>
.apexcharts-canvas .apexcharts-toolbar {
  z-index: 1;
}
</style>